import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslationService } from '../../services/translation/translation.service';
import {Config} from "../../services/config/config.service";

@Pipe({
    name: 'dateFormat',
    pure: false,
    standalone: true
})
@Injectable()
export class DateFormatPipe implements PipeTransform {
  // Defines the respective suffixes for days of the month (e.g., `1st`, `2nd`, `3rd`, etc.).
  private readonly dayEndings: Record<number, string> = {
    1: 'st', 2: 'nd', 3: 'rd', 21: 'st', 22: 'nd', 23: 'rd', 31: 'st'
  };

  constructor(
    private readonly translationService: TranslationService,
    private readonly translateService: TranslocoService,
    private readonly config: Config
  ) {}

  /**
   * Returns the day-ending suffix (e.g., `st`, `nd`, `rd`, or `th`) for a provided day of the month.
   * @param day The day of the month (e.g., 1, 21, 30).
   * @returns The appropriate suffix for the day.
   */
  private getDayEnding(day: number): string {
    return this.dayEndings[day] || 'th';
  }

  /**
   * Retrieves the timezone short format (e.g., `PST`, `EST`) from a given date object.
   * Includes fallback mechanisms if specific locale retrieval fails.
   * @param date The `Date` object.
   * @returns A timezone string, e.g., `PST`.
   */
  private getTimeZoneOffset(date: Date): string {
    try {
      return /.*\s(.+)/.exec(date.toLocaleDateString(navigator.language, { timeZoneName: 'short' }))?.[1] || '';
    } catch {
      return date.toTimeString().match(/[A-Z](?!.*[\(])/g)?.join('') || '';
    }
  }

  /**
   * Adjusts a date to match the browser's local timezone.
   * This accounts for differences between UTC and the user's timezone.
   * @param date The `Date` object to adjust.
   * @returns A new `Date` object shifted to local timezone.
   */
  private adjustToBrowserTimeZone(date: Date): Date {
    const timeZoneOffset = (date.getTimezoneOffset() / 60) * -1;
    date.setHours(date.getHours() + timeZoneOffset);
    return date;
  }

  /**
   * Generates a US-specific date format based on the provided type and date components.
   * @param type The date format type (e.g., `long`, `short`, etc.).
   * @param components The extracted components from the date (e.g., day, month, time).
   * @returns The formatted date string.
   */
  private getUSDateFormat(type: string, components: Record<string, any>): string {
    const formats: Record<string, string> = {
      time: 'UST TZ',
      longYear: 'mmm ddd DD, YY',
      day_month: 'mmm, ddd DD',
      month_day: 'mmm DD',
      day_hour: 'ddd, UST TZ',
      full: 'MM/DD/YY UST TZ',
      comments: 'mmm ddd DD, UST TZ',
      long: 'ddd, mmm DD UST TZ', // 'ddd, mmm DD TT'
      short: 'ddd, UST TZ', // Mon, 1:20 AM/PM (US don't prefix with 0)
    };
    const dateFormat = formats[type] || this.translationService.getDefaultDateFormat();
    return dateFormat.replace(/ddd|DD|mmm|MM|TT|TZ|YY|AM_PM|hhMM|UST|term/gi, match => components[match]);
  }

  /**
   * Generates a localized date format based on the provided type and date components.
   * This supports custom formats from the translation service for more flexibility.
   * @param type The date format type (e.g., `short`, `long`, etc.).
   * @param components The extracted components from the date (e.g., day, month, time).
   * @returns The formatted date string.
   */
  private getLocalizedDateFormat(type: string, components: Record<string, any>): string {
    const formats: Record<string, string> = {
      time: 'TT',
      longYear: 'ddd DD, mmm YY',
      day_month: 'ddd, DD mmm',
      month_day: 'mmm DD',
      day_hour: 'ddd, TT TZ',
      full: 'DD/MM/YY TT',
      comments: 'ddd DD mmm, TT',
      long: this.translationService.getLongDateFormat(), // 'ddd, TT'
      short: this.translationService.getShortDateFormat(), // 'ddd, mmm DD TT'
    };
    const dateFormat = formats[type] || this.translationService.getDefaultDateFormat();
    return dateFormat.replace(/ddd|DD|mmm|MM|TT|TZ|YY|AM_PM|hhMM|UST|term/gi, match => components[match]);
  }

  /**
   * Main transformation method of the pipe. It formats a date string into a localized or US-specific format
   * depending on the application configuration.
   * @param date The input date, as a `Date` object or a string `Date` can parse.
   * @param type The desired date format type (e.g., `short`, `long`, `full`).
   * @returns The formatted date string in the configured style.
   */
  transform(date: Date | string, type: string = 'default'): string {
    // Always construct a fresh Date so adjustToBrowserTimeZone's in-place mutation
    // never touches a Date instance owned by the caller.
    const browserDate = this.adjustToBrowserTimeZone(date instanceof Date ? new Date(date.getTime()) : new Date(date));
    const timeZoneOffset = this.getTimeZoneOffset(browserDate);

    // Extracts reusable components for constructing date formats
    const dateComponents = {
      ddd: this.translateService.translate('datetime.DAY_' + browserDate.getUTCDay()),
      DD: browserDate.getUTCDate(),
      mmm: this.translateService.translate('datetime.MONTH_' + browserDate.getUTCMonth()),
      MM: browserDate.getUTCMonth() + 1,
      YY: browserDate.getUTCFullYear(),
      TT: browserDate.toISOString().slice(11, 16), // HH:mm format
      TZ: timeZoneOffset,
      term: this.getDayEnding(browserDate.getUTCDate()),
      AM_PM: browserDate.getUTCHours() >= 12 ? 'PM' : 'AM',
      hhMM: `${(browserDate.getUTCHours() % 12 || 12)
        .toString()
        .padStart(2, '0')}:${browserDate.getUTCMinutes()
        .toString()
        .padStart(2, '0')}`, // 12-hour format HH:mm
      UST: `${(browserDate.getUTCHours() % 12 || 12)}:${browserDate.getUTCMinutes().toString().padStart(2, '0')} ${browserDate.getUTCHours() >= 12 ? 'PM' : 'AM'}`,
    };

    return this.config.CONFIG.usDateFormat ? this.getUSDateFormat(type as string, dateComponents) : this.getLocalizedDateFormat(type as string, dateComponents);

  }
}
